/**
 * 埋点事件定义与工具函数（单一职责）
 * 基于 Umami 统计平台
 * 所有埋点事件统一从这里发出，业务组件不直接调用 umami
 */
import { track, identify, setSessionData } from './umami';

// ============ 用户属性 ============

/** 设置用户属性（语言偏好、来源等） */
export function setUserProperties(props: Record<string, unknown>) {
  setSessionData(props);
}

/** 识别用户（申请提交后可关联） */
export function identifyUser(email: string, props?: Record<string, unknown>) {
  identify(email, props);
}

// ============ 全局行为 ============

export function trackLanguageSwitched(from: string, to: string) {
  track('language-switch', { from, to });
  setSessionData({ preferred_language: to });
}

export function trackNavClicked(item: string) {
  track('nav-click', { item });
}

// ============ 首页 ============

export function trackSearchQuery(query: string, resultCount: number) {
  track('search', { query, results: resultCount });
}

export function trackCityClicked(cityId: string, cityName: string, source: string) {
  track('city-click', { city: cityName, city_id: cityId, source });
}

export function trackHotJobClicked(jobId: string, jobTitle: string, position: number) {
  track('hot-job-click', { job_id: jobId, title: jobTitle, position });
}

export function trackRemoteBannerClicked() {
  track('remote-banner-click');
}

export function trackHomebasedBannerClicked() {
  track('homebased-banner-click');
}

export function trackCtaClicked(button: 'primary' | 'secondary') {
  track('cta-click', { button });
}


// ============ 职位列表页 ============

export function trackJobFilterApplied(filters: {
  cities?: string[];
  departments?: string[];
  types?: string[];
  workStyles?: string[];
}) {
  track('filter-apply', {
    cities: filters.cities?.join(',') || '',
    departments: filters.departments?.join(',') || '',
    types: filters.types?.join(',') || '',
    work_styles: filters.workStyles?.join(',') || '',
  });
}

export function trackJobSearch(query: string, resultCount: number) {
  track('job-search', { query, results: resultCount });
}

export function trackJobCardClicked(jobId: string, jobTitle: string, source: string) {
  track('job-click', { job_id: jobId, title: jobTitle, source });
}

// ============ 职位详情页 ============

export function trackJobViewed(job: { id: string; title: string; department: string; city: string; type: string; workStyle: string }) {
  track('job-view', {
    job_id: job.id,
    title: job.title,
    department: job.department,
    city: job.city,
    type: job.type,
    work_style: job.workStyle,
  });
}

export function trackJobShared(jobId: string, jobTitle: string) {
  track('job-share', { job_id: jobId, title: jobTitle });
}

export function trackJobCityClicked(jobId: string, cityId: string) {
  track('job-city-click', { job_id: jobId, city_id: cityId });
}

export function trackApplyClicked(jobId: string, jobTitle: string, source: 'detail_button' | 'sticky_bar') {
  track('apply-click', { job_id: jobId, title: jobTitle, source });
}

// ============ 申请流程 ============

export function trackApplicationStarted(jobId: string, jobTitle: string) {
  track('apply-start', { job_id: jobId, title: jobTitle });
}

export function trackApplicationStepCompleted(jobId: string, step: number) {
  track('apply-step', { job_id: jobId, step });
}

export function trackResumeUploaded(jobId: string, fileType: string, fileSizeMb: number) {
  track('resume-upload', { job_id: jobId, file_type: fileType, size_mb: fileSizeMb });
}

export function trackApplicationSubmitted(jobId: string, jobTitle: string) {
  track('apply-submit', { job_id: jobId, title: jobTitle });
}

export function trackApplicationAbandoned(jobId: string, step: number) {
  // Umami 在 beforeunload 时不可靠，保留接口但不发送
  // 可通过 Funnel 的 drop-off 分析流失
}

export function trackApplicationSuccessViewed(jobId: string) {
  track('apply-success', { job_id: jobId });
}

export function trackFormValidationError(jobId: string, step: number, fields: string[]) {
  track('form-error', { job_id: jobId, step, fields: fields.join(',') });
}

// ============ 辅助页面 ============

export function trackCityViewed(cityId: string, cityName: string) {
  track('city-view', { city_id: cityId, city_name: cityName });
}

export function trackOfficesViewed() {
  track('offices-view');
}

export function trackLifePageViewed() {
  track('life-view');
}
